import { useState } from "react";
import { Link } from "react-router-dom";

export type AlertItem = {
  id: string;
  severity: "danger" | "warning" | "info";
  icon: string;
  title: string;
  desc: string;
  href: string;
  action?: string;
};

interface Props {
  alerts: AlertItem[];
}

const STYLES: Record<string, { wrap: string; iconBg: string; iconColor: string; badge: string; dot: string }> = {
  danger: {
    wrap: "bg-red-50 border-red-200 hover:border-red-300",
    iconBg: "bg-red-400/15",
    iconColor: "text-red-400",
    badge: "bg-red-400/15 text-red-400 hover:bg-red-400/25",
    dot: "bg-red-400",
  },
  warning: {
    wrap: "bg-amber-50 border-amber-200 hover:border-amber-300",
    iconBg: "bg-amber-400/15",
    iconColor: "text-amber-400",
    badge: "bg-amber-400/15 text-amber-400 hover:bg-amber-400/25",
    dot: "bg-amber-400",
  },
  info: {
    wrap: "bg-sky-50 border-sky-200 hover:border-sky-300",
    iconBg: "bg-sky-400/15",
    iconColor: "text-sky-400",
    badge: "bg-sky-400/15 text-sky-400 hover:bg-sky-400/25",
    dot: "bg-sky-400",
  },
};

export default function DashboardAlerts({ alerts }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const visible = alerts.filter((a) => !dismissed.has(a.id));

  if (visible.length === 0) {
    return (
      <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-emerald-50 border border-emerald-200">
        <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-400 shrink-0">
          <i className="ri-checkbox-circle-line text-sm"></i>
        </span>
        <div>
          <p className="text-emerald-400 text-sm font-semibold">All clear — no active alerts</p>
          <p className="text-emerald-400/50 text-xs mt-0.5">Your studio is running smoothly.</p>
        </div>
      </div>
    );
  }

  const dangerCount  = visible.filter((a) => a.severity === "danger").length;
  const warningCount = visible.filter((a) => a.severity === "warning").length;

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-5 h-5 flex items-center justify-center text-amber-400">
          <i className="ri-alert-line text-sm"></i>
        </span>
        <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Active Alerts</p>
        <div className="flex items-center gap-1.5">
          {dangerCount > 0 && (
            <span className="bg-red-400/20 text-red-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {dangerCount} critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="bg-amber-400/20 text-amber-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {warningCount} warning
            </span>
          )}
        </div>
      </div>

      {/* Alert rows */}
      {visible.map((alert) => {
        const s = STYLES[alert.severity] ?? STYLES.info;
        return (
          <div
            key={alert.id}
            className={`flex items-center gap-4 px-4 py-3 rounded-xl border transition-all ${s.wrap}`}
          >
            <span className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${s.iconBg} ${s.iconColor}`}>
              <i className={`${alert.icon} text-sm`}></i>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[#0f172a] text-sm font-medium leading-snug">{alert.title}</p>
              <p className="text-slate-400 text-xs mt-0.5 truncate">{alert.desc}</p>
            </div>
            <Link
              to={alert.href}
              className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${s.badge}`}
            >
              {alert.action ?? "View"}
            </Link>
            <button
              onClick={() => setDismissed((prev) => new Set([...prev, alert.id]))}
              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer shrink-0"
              title="Dismiss"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        );
      })}
    </div>
  );
}
